import type { ComponentType, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { CMP_BASE_URL } from '@frontend/envConfig';

type CmpEmbedProviderProps = {
  baseUrl: string;
  children: ReactNode;
  onTokenExpired?: () => void;
  orgSsoId: string;
  token: string;
};

type EmbeddableTaskProps = {
  className?: string;
  defaultTab?: 'content' | 'history';
  onApprove?: () => void;
  onReject?: (reason: string) => void;
  taskId: string;
};

type CmpModules = {
  CmpEmbedProvider: ComponentType<CmpEmbedProviderProps>;
  EmbeddableTask: ComponentType<EmbeddableTaskProps>;
};

let cached: CmpModules | null = null;
let inflight: Promise<CmpModules> | null = null;
let polyfilled = false;

async function loadModules(): Promise<CmpModules> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    // Phase 1: load the globals the CMP remotes expect, and install them on
    // `window` BEFORE importing the remotes. The MFECmp/* modules reference
    // ambient `_` / `moment` / `window.features` at module-evaluation time, so
    // they must be polyfilled first — otherwise evaluating the remote throws
    // `ReferenceError: _ is not defined`.
    const [moment, _] = await Promise.all([import('moment'), import('lodash-es')]);

    if (!polyfilled) {
      window.moment = moment.default ?? moment;
      // @ts-expect-error -- stub lodash for CMP
      window._ = _;
      // @ts-expect-error -- stub window.features for CMP (feature-flag gate it reads at module load)
      window.features ??= 'axiom,rebrand';
      // @ts-expect-error -- stub the CMP embed base URL so CMP's socket connects
      // to the CMP origin (ws.<cmp-host>) instead of deriving it from the host
      // app's window.origin. Must be set before the remote is imported because
      // the socket reads it at module-evaluation time. See cmp-client#12055.
      window.CMP_EMBED_BASE_URL ??= CMP_BASE_URL;
      // @ts-expect-error -- stub window.analytics for CMP
      window.analytics ??= {
        group: () => {},
        identify: () => {},
        load: () => {},
        page: () => {},
        track: () => {},
      };
      polyfilled = true;
    }

    // Phase 2: now that the globals exist, import the CMP remotes.
    const [{ default: CmpEmbedProvider }, { default: EmbeddableTask }] = await Promise.all([
      import('MFECmp/CmpEmbedProvider'),
      import('MFECmp/EmbeddableTask'),
    ]);

    cached = { CmpEmbedProvider, EmbeddableTask };
    inflight = null;
    return cached;
  })();

  return inflight;
}

export function useCmpModules(load: boolean) {
  const [modules, setModules] = useState<CmpModules | null>(cached);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!load) return;
    if (cached) {
      setModules(cached);
      return;
    }
    let cancelled = false;
    loadModules()
      .then(m => {
        if (!cancelled) setModules(m);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { ...modules, isLoaded: modules !== null, error };
}
